import React, { useEffect, useMemo, useState } from 'react';
import { CreateAccountBtnWrapper, PageContainer, PageContent, PageWrapper, StyledButton } from '../../assets/styles/Styled';
import { Link } from 'react-router-dom';
import { message, Space } from 'antd';
import { deleteScript, getScripts } from '../../api/apiWorker';
import { FilterOptions, OrganizationItem, Script, SortOptions } from '../../types';
import { Search } from '../../components/Search';
import { FilterItems, FilterPanel } from './FilterPanel';
import { ScriptsTable } from './ScriptsTable';
import { extractOrganizationsFromScripts } from '../../helper';

interface Filters {
  search: string;
  filter: FilterItems;
}

const ScriptsPage = () => {
  const [loading, setLoading] = useState(false);
  const [scripts, setScripts] = useState<Script[]>([]);
  const [scriptOrganizations, setScriptOrganizations] = useState<OrganizationItem[]>([]);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    filter: { option: FilterOptions.all, selectedOrganizations: null }
  });
  const [sortOption, setSortOption] = useState<SortOptions>(SortOptions.newest);

  const fetchScripts = async () => {
    setLoading(true);
    try {
      const data = await getScripts();
      if (Array.isArray(data)) {
        setScripts(data);
        const organizationList = extractOrganizationsFromScripts(data);
        setScriptOrganizations(organizationList);
      }
    } catch (e) {
      message.error('Error fetching scripts');
      console.log(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchScripts();
  }, []);

  const filteredScripts: Script[] = useMemo(() => {
    let result = scripts.filter(script => script.script_name.toLowerCase().includes(filters.search.toLowerCase()));
    const { option, selectedOrganizations } = filters.filter;
    if (option === FilterOptions.withoutOrganization) {
      result = result.filter(script => !script.organizations?.length);
    }
    if (selectedOrganizations?.length) {
      result = result.filter(script => {
        return script.organizations?.some(org => selectedOrganizations.includes(org.organization_id));
      });
    }

    switch (sortOption) {
      case SortOptions.newest:
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        break;
      case SortOptions.eldest:
        result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case SortOptions.az:
        result.sort((a, b) => a.script_name.localeCompare(b.script_name));
        break;
      case SortOptions.za:
        result.sort((a, b) => b.script_name.localeCompare(a.script_name));
        break;
      default:
        break;
    }

    return result;
  }, [scripts, filters, sortOption]);

  const handleRemove = async (scriptId: string) => {
    setLoading(true);
    try {
      await deleteScript(scriptId);
      setScripts(prev => prev.filter(v => v.script_id !== scriptId));
      message.success('Script was deleted successfully');
    } catch (e) {
      message.error('Unable to delete script');
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper>
      <PageContainer>
        <PageContent>
          <Space direction="vertical" size={24} className="w-100">
            <CreateAccountBtnWrapper className="btn-script">
              <Link to="/scripts/new">
                <StyledButton className="btn-green">Create Script</StyledButton>
              </Link>
            </CreateAccountBtnWrapper>
            <Search placeholder="Created Scripts" onChange={search => setFilters(prev => ({ ...prev, search }))} />
          </Space>
          <FilterPanel
            organizations={scriptOrganizations}
            onSort={opt => setSortOption(opt)}
            onFilterChange={filter => setFilters(prev => ({ ...prev, filter }))}
          />
          <ScriptsTable tableData={filteredScripts} loading={loading} onRemove={handleRemove} />
        </PageContent>
      </PageContainer>
    </PageWrapper>
  );
};

export default ScriptsPage;
